import React from 'react';

import { useQuery } from '@apollo/client';
import { Helmet } from 'react-helmet';

import DataLoader from '@ivy/components/molecules/DataLoader';
import { RouteUri } from '@ivy/constants/routes';
import { gql } from '@ivy/gql/types';
import {
	ZOOM_FACILITY,
	calculateRecommendedViewport,
	ZOOM_MAX_LOCALITY,
} from '@ivy/lib/services/maps';
import { buildInternalLink } from '@ivy/lib/util/route';
import useWhitelabel from '@ivy/lib/whitelabel/useWhitelabel';
import FacilityMap from '@ivy/views/facility/FacilityMap';

const OrgFacilityList_OrgQDoc = gql(/* GraphQL */ `
	query OrgFacilityList_Org($orgId: uuid!) {
		org: organization_by_pk(id: $orgId) {
			id
			name
			bounds {
				id
				xMin: x_min
				xMax: x_max
				yMin: y_min
				yMax: y_max
			}
		}
	}
`);

export interface OrgFacilityListProps {
	orgId: string;
}

const OrgFacilityList = ({ orgId }: OrgFacilityListProps) => {
	const wl = useWhitelabel();
	const { data, loading, error } = useQuery(OrgFacilityList_OrgQDoc, {
		variables: {
			orgId,
		},
	});

	const resolvePageTitle = (loc?: string, prof?: string) => {
		if (prof) {
			return `EM ${prof} Jobs${loc ? ` in ${loc}` : ''}`;
		}
		return 'Search jobs';
	};

	const resolveFeaturedTitle = () => {
		return 'Current Openings';
	};

	const resolveResultsTitle = () => {
		return 'Inquire About Openings';
	};

	const resolveHelmet = (loc?: string, prof?: string) => {
		if (loc && prof) {
			const title = `Emergency ${prof} Jobs in ${loc} | ${wl.productName}`;
			const description = `Apply for Emergency ${prof} jobs in ${loc}. Finding your next emergency medicine job has never been easier.`;

			return (
				<Helmet>
					{!!title && <title>{title}</title>}
					{!!description && <meta name='description' content={description} />}
				</Helmet>
			);
		}
		return null;
	};

	return (
		<DataLoader
			variant='circular'
			data={data}
			loading={loading}
			error={error}
			keys={['org']}
		>
			{() => (
				<FacilityMap
					includeJobs
					resolvePageTitle={resolvePageTitle}
					resolveFeaturedTitle={resolveFeaturedTitle}
					resolveResultsTitle={resolveResultsTitle}
					resolveHelmet={resolveHelmet}
					clustering
					maxViewportZoom={ZOOM_MAX_LOCALITY}
					maxZoom={ZOOM_FACILITY}
					types={['place']}
					baseRoute={buildInternalLink(RouteUri.ORG_FACILITY_LIST, {
						orgId: orgId,
					})}
					pageRoute={buildInternalLink(RouteUri.ORG_FACILITY_LIST_PAGE, {
						orgId: orgId,
					})}
					pageTitleComponent='h2'
					defaultLocation={{
						id: `precise-org-${orgId}`,
						type: 'precise',
						label: '',
						center: {
							lat: (data!.org!.bounds!.yMin + data!.org!.bounds!.yMax) / 2,
							lng: (data!.org!.bounds!.xMin + data!.org!.bounds!.xMax) / 2,
						},
						viewport: calculateRecommendedViewport({
							xMin: data!.org!.bounds!.xMin,
							xMax: data!.org!.bounds!.xMax,
							yMin: data!.org!.bounds!.yMin,
							yMax: data!.org!.bounds!.yMax,
						}),
					}}
					rootFilters={{
						contracts: {
							active: {
								_eq: true,
							},
							org_id: {
								_eq: orgId,
							},
						},
					}}
				/>
			)}
		</DataLoader>
	);
};

export default OrgFacilityList;
